import React from 'react';
import { Payment } from '../types/workflowTypes';
import { formatCurrency, formatDate as formatDateCentral } from '../utils/formatters';

interface WorkflowPaymentHistoryCardProps {
  payments: Payment[];
  currency?: string;
  totalAmount?: number;
}

/**
 * Eine Komponente zur Anzeige der Zahlungshistorie eines Workflows
 */
const WorkflowPaymentHistoryCard: React.FC<WorkflowPaymentHistoryCardProps> = ({ 
  payments, 
  currency = 'EUR',
  totalAmount
}) => {
  if (!payments || payments.length === 0) {
    return null;
  }

  // Berechne Gesamtbetrag der Zahlungen
  const totalPaid = payments.reduce((sum, payment) => sum + payment.amount, 0);
  
  // Berechne verbleibenden Betrag, falls totalAmount angegeben ist
  const remainingAmount = totalAmount !== undefined ? Math.max(0, totalAmount - totalPaid) : undefined;
  
  // Sortiere Zahlungen nach Datum (neueste zuerst)
  const sortedPayments = [...payments].sort((a, b) => {
    const dateA = new Date(a.paymentDate);
    const dateB = new Date(b.paymentDate);
    return dateB.getTime() - dateA.getTime();
  });

  const safeFormatDate = (dateInput: any): string => formatDateCentral(dateInput);

  return (
    <div className="bg-surface-raised shadow-lg rounded-xl p-6 mb-6 border border-hairline">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-primary">Zahlungshistorie</h2>
        <div className="text-right">
          <p className="text-sm text-muted">
            Gesamt bezahlt: <span className="text-primary font-medium ml-1">
              {formatCurrency(totalPaid, currency)}
            </span>
          </p>
          {remainingAmount !== undefined && (
            <p className="text-sm text-muted">
              Verbleibend: <span className={`font-medium ml-1 ${remainingAmount > 0 ? 'text-[var(--tone-danger-fg)]' : 'text-[var(--tone-success-fg)]'}`}>
                {formatCurrency(remainingAmount, currency)}
              </span>
            </p>
          )}
        </div>
      </div>

      <div className="space-y-4 divide-y divide-[color:var(--border-hairline)]">
        {sortedPayments.map((payment, index) => (
          <div key={index} className={`${index > 0 ? 'pt-4' : ''}`}>
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-1 text-xs rounded ${payment.paymentType === 'full' ? 'bg-[var(--tone-success-bg)] text-[var(--tone-success-fg)]' : 'bg-[var(--tone-warning-bg)] text-[var(--tone-warning-fg)]'}`}>
                    {payment.paymentType === 'full' ? 'Vollständige Zahlung' : 'Teilzahlung'}
                  </span>
                  <span className="text-primary font-medium">{formatCurrency(payment.amount, payment.currency || currency)}</span>
                </div>
                <p className="text-sm text-muted mt-1">
                  Zahlungsdatum: {safeFormatDate(payment.paymentDate)}
                </p>
                {payment.documentNumber && (
                  <p className="text-sm text-muted">
                    Dokument: {payment.documentNumber}
                  </p>
                )}
                {payment.paymentMethod && (
                  <p className="text-xs text-faint mt-1">
                    Zahlungsart: {payment.paymentMethod}
                  </p>
                )}
              </div>
              <div className="text-right">
                <p className="text-sm text-muted">
                  Erfasst am {safeFormatDate(payment.createdAt)}
                </p>
                {payment.note && (
                  <p className="text-sm text-secondary mt-1 bg-surface-inset p-2 rounded border-l-2 border-yellow-500 max-w-xs">
                    {payment.note}
                  </p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default WorkflowPaymentHistoryCard; 